import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { useClient } from '@/hooks/context/useClient';
import { useAuthState } from '@/hooks/auth/useAuth';
import { ClientType } from '@ata-digital/client-tax-system-api-client';
import { clientTokenManager } from '@/lib/auth/tokenManager';
import { resolveClientDestination } from '@/utils/clientRouting';
import type { Theme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Container,
  Stack,
  Chip,
  Avatar,
  useTheme,
  useMediaQuery,
  Button,
  alpha,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Groups as GroupsIcon,
  AccountBalance as TrustIcon,
  HomeWork as EstateIcon,
  ArrowForward as ArrowForwardIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { containerVariants, cardVariants, fabVariants } from '@/lib/animations/variants';
import { maskTaxIdForDisplay } from '@/utils/taxIdValidation';

export const Route = createFileRoute('/client-select')({
  component: ClientSelectPage,
});

/**
 * Maps ClientType enum from API to display icon
 * Handles all ClientType values including Corporation, EmployeePlan, Fiduciary, etc.
 */
function getClientIcon(type?: ClientType) {
  switch (type) {
    case ClientType.Individual:
      return <PersonIcon />;
    case ClientType.Corporation:
    case ClientType.SoleProprietorship:
    case ClientType.NonProfit:
      return <BusinessIcon />;
    case ClientType.Partnership:
      return <GroupsIcon />;
    case ClientType.Fiduciary:
      return <TrustIcon />;
    case ClientType.EmployeePlan:
      return <EstateIcon />;
    case ClientType.Other:
    case ClientType.Staff:
    case ClientType.Project:
    default:
      return <BusinessIcon />;
  }
}

/**
 * Maps ClientType enum to theme color
 */
function getClientColor(type: ClientType | undefined, theme: Theme): string {
  switch (type) {
    case ClientType.Individual:
      return theme.palette.primary.main;
    case ClientType.Corporation:
    case ClientType.SoleProprietorship:
      return theme.palette.success.main;
    case ClientType.Partnership:
      return theme.palette.info.main;
    case ClientType.Fiduciary:
      return theme.palette.warning.main;
    case ClientType.EmployeePlan:
    case ClientType.NonProfit:
      return theme.palette.secondary.main;
    case ClientType.Other:
    case ClientType.Staff:
    case ClientType.Project:
    default:
      return theme.palette.grey[500];
  }
}

/**
 * Determines tax ID type based on ClientType
 * Individual = SSN, all others = EIN
 */
function getTaxIdType(type?: ClientType): 'SSN' | 'EIN' {
  return type === ClientType.Individual ? 'SSN' : 'EIN';
}

/**
 * Client selection page component
 * - Fetches available clients from backend API
 * - Displays loading skeleton while fetching
 * - Shows error state with retry button on failure
 * - Auto-redirects if user has only 1 client
 * - Shows empty state if no clients available
 *
 * Exported so it can be unit-tested directly without booting the full router.
 */
export function ClientSelectPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const { setSelectedClient, availableClients, isLoadingClients, clientsError, refetchClients } = useClient();

  // Check auth loading state to prevent race condition where clients query
  // is disabled (and returns empty) while auth is still being determined
  const { isLoading: isAuthLoading } = useAuthState();
  const [isNavigating, setIsNavigating] = useState(false);

  /**
   * Select a client, resolve their binder destination, and navigate directly.
   * Keeps the user on the client-select page (showing skeleton) until the final page is ready.
   *
   * Wrapped in useCallback so it can safely be a dependency of the auto-redirect
   * effect below — this prevents stale-closure bugs when `availableClients` updates
   * (e.g. a slower clients query resolves after auth does).
   */
  const selectClientAndNavigate = useCallback(
    async (clientId: string) => {
      const token = clientTokenManager.getStoredToken();
      const client = availableClients.find((c) => c.id === clientId);

      if (!token || !client) return;

      setIsNavigating(true);

      try {
        // Cancel and remove any cached binder queries to prevent stale data
        await queryClient.cancelQueries({ queryKey: ['taxBinders'] });
        queryClient.removeQueries({ queryKey: ['taxBinders'] });

        setSelectedClient(client);

        const destination = await resolveClientDestination(queryClient, token, clientId);
        navigate(destination as Parameters<typeof navigate>[0]);
      } finally {
        setIsNavigating(false);
      }
    },
    [availableClients, queryClient, setSelectedClient, navigate]
  );

  /**
   * Auto-redirect if user has exactly one client
   */
  useEffect(() => {
    if (
      !isAuthLoading &&
      !isLoadingClients &&
      !clientsError &&
      availableClients.length === 1 &&
      availableClients[0].id
    ) {
      void selectClientAndNavigate(availableClients[0].id);
    }
  }, [availableClients, isAuthLoading, isLoadingClients, clientsError, selectClientAndNavigate]);

  const handleClientSelect = async (clientId: string) => {
    setSelectedClientId(clientId);
    await selectClientAndNavigate(clientId);
  };

  /**
   * Handle continue button (mobile only)
   */
  const handleContinue = async () => {
    if (selectedClientId) {
      await selectClientAndNavigate(selectedClientId);
    }
  };

  /**
   * Handle retry on error
   */
  const handleRetry = () => {
    refetchClients();
  };

  // Loading State - show skeleton while auth or clients are loading,
  // or while resolving the binder destination after client selection
  if (isAuthLoading || isLoadingClients || isNavigating) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 6 } }}>
        {/* Header Skeleton */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
          <Skeleton variant="text" width={300} height={60} sx={{ mx: 'auto' }} />
          <Skeleton variant="text" width={500} height={24} sx={{ mx: 'auto', mt: 1 }} />
        </Box>

        {/* Client Cards Grid Skeleton */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: { xs: 2, sm: 3 },
          }}
        >
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="80%" height={32} />
                    <Skeleton variant="text" width="60%" height={20} />
                  </Box>
                </Box>
                <Stack spacing={1}>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="100%" height={20} />
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>
    );
  }

  // Error State
  if (clientsError) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 6 } }}>
        <Alert
          severity="error"
          icon={<ErrorIcon />}
          sx={{
            mb: 3,
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <Typography variant="h6" gutterBottom>
            Unable to Load Clients
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            We're having trouble loading your client information. Please try again or contact your CPA if the problem
            persists.
          </Typography>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRetry}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  // Empty State
  if (availableClients.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 6 } }}>
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              No Clients Available
            </Typography>
            <Typography variant="body2">
              No client accounts are currently available. Please contact your CPA for further instructions.
            </Typography>
          </Alert>
        </Box>
      </Container>
    );
  }

  // Main Content - Client Selection
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 6 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
          }}
        >
          Select a Client
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          You have access to {availableClients.length} client{availableClients.length !== 1 ? 's' : ''}. Please select
          the client you'd like to view.
        </Typography>
      </Box>

      {/* Client Cards Grid */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: isMobile ? '16px' : '24px',
          marginBottom: '32px',
        }}
      >
        {availableClients.map((client) => {
          const clientColor = getClientColor(client.type, theme);
          const taxIdType = getTaxIdType(client.type);

          return (
            <motion.div key={client.id} variants={cardVariants} whileHover="hover" whileTap="tap">
              <Card
                sx={{
                  position: 'relative',
                  border: selectedClientId === client.id ? 2 : 1,
                  borderColor: selectedClientId === client.id ? theme.palette.primary.main : 'divider',
                }}
              >
                <CardActionArea onClick={() => handleClientSelect(client.id || '')}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    {/* Client Type Chip */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Chip
                        label={client.typeDisplay || 'Client'}
                        size="small"
                        sx={{
                          backgroundColor: alpha(clientColor, 0.1),
                          color: clientColor,
                          fontWeight: 500,
                        }}
                      />
                    </Box>

                    {/* Client Icon and Name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(clientColor, 0.1),
                          color: clientColor,
                          width: { xs: 48, sm: 56 },
                          height: { xs: 48, sm: 56 },
                          mr: 2,
                        }}
                      >
                        {getClientIcon(client.type)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: '1rem', sm: '1.125rem' },
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {client.name || 'Unnamed Client'}
                        </Typography>
                        {client.maskedTaxId && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {taxIdType}: {maskTaxIdForDisplay(client.maskedTaxId, taxIdType)}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Additional Info */}
                    <Stack spacing={1}>
                      {client.organizerCount !== undefined && client.organizerCount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            Tax Binders:
                          </Typography>
                          <Typography variant="caption" fontWeight={500}>
                            {client.organizerCount}
                          </Typography>
                        </Box>
                      )}
                      {client.auditBinderCount !== undefined && client.auditBinderCount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            Audit Binders:
                          </Typography>
                          <Typography variant="caption" fontWeight={500}>
                            {client.auditBinderCount}
                          </Typography>
                        </Box>
                      )}
                      {client.lastActivityAt && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            Last Activity:
                          </Typography>
                          <Typography variant="caption" fontWeight={500}>
                            {new Date(client.lastActivityAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                      {client.documentCount !== undefined && client.documentCount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">
                            Documents:
                          </Typography>
                          <Typography variant="caption" fontWeight={500}>
                            {client.documentCount}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    {/* Selection Indicator */}
                    {selectedClientId === client.id && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        ✓
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Continue Button - Mobile Only */}
      <AnimatePresence>
        {isMobile && selectedClientId && (
          <motion.div
            variants={fabVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000 }}
          >
            <Box sx={{ p: 2, bgcolor: 'background.paper', boxShadow: 3 }}>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleContinue}
                endIcon={<ArrowForwardIcon />}
              >
                Continue
              </Button>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Actions */}
      <Box
        sx={{
          mt: 6,
          pt: 4,
          borderTop: 1,
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Don't see the client you're looking for? Please contact your CPA for assistance.
        </Typography>
      </Box>
    </Container>
  );
}
